import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Tag, Percent, Calendar, Check, X } from 'lucide-react';
import api from '../../utils/api';

const DiscountManagement = () => {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);

  useEffect(() => {
    fetchDiscounts();
  }, []);

  const fetchDiscounts = async () => {
    try {
      const res = await api.get('/discounts/admin');
      if (res.data.success) {
        setDiscounts(res.data.data);
      }
    } catch (error) {
      console.error('Error fetching discounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this discount code?')) return;

    try {
      await api.delete(`/discounts/admin/${id}`);
      setDiscounts(discounts.filter(d => d._id !== id));
    } catch (error) {
      alert('Error deleting discount: ' + error.response?.data?.message);
    }
  };

  const toggleActive = async (discount) => {
    try {
      await api.put(`/discounts/admin/${discount._id}`, {
        isActive: !discount.isActive
      });
      fetchDiscounts();
    } catch (error) {
      alert('Error updating discount: ' + error.response?.data?.message);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-5xl font-black tracking-tighter mb-2">DISCOUNTS</h1>
          <div className="inline-block bg-yellow-400 text-black px-4 py-1 font-mono font-bold text-xs border-2 border-black">
            COUPON_MANAGEMENT
          </div>
        </div>
        <button
          onClick={() => {
            setEditingDiscount(null);
            setShowModal(true);
          }}
          className="flex items-center gap-2 bg-black text-white px-6 py-3 font-black border-4 border-yellow-400 shadow-[8px_8px_0px_rgba(255,255,0,1)] hover:translate-x-1 hover:translate-y-1 hover:shadow-[4px_4px_0px_rgba(255,255,0,1)] transition-all"
        >
          <Plus size={20} />
          CREATE COUPON
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-green-50 border-4 border-black p-4">
          <div className="text-3xl font-black">{discounts.filter(d => d.isActive).length}</div>
          <div className="text-sm font-bold text-gray-600">ACTIVE COUPONS</div>
        </div>
        <div className="bg-red-50 border-4 border-black p-4">
          <div className="text-3xl font-black">{discounts.filter(d => !d.isActive).length}</div>
          <div className="text-sm font-bold text-gray-600">INACTIVE</div>
        </div>
        <div className="bg-blue-50 border-4 border-black p-4">
          <div className="text-3xl font-black">{discounts.reduce((sum, d) => sum + d.usedCount, 0)}</div>
          <div className="text-sm font-bold text-gray-600">TOTAL USES</div>
        </div>
        <div className="bg-purple-50 border-4 border-black p-4">
          <div className="text-3xl font-black">{discounts.filter(d => new Date(d.validUntil) < new Date()).length}</div>
          <div className="text-sm font-bold text-gray-600">EXPIRED</div>
        </div>
      </div>

      {/* Discounts List */}
      {loading ? (
        <div className="text-center py-20">
          <div className="text-2xl font-black animate-pulse">LOADING_COUPONS...</div>
        </div>
      ) : discounts.length === 0 ? (
        <div className="bg-white border-4 border-black p-12 text-center">
          <Tag size={64} className="mx-auto mb-4 text-gray-300" />
          <div className="text-gray-400 font-black text-2xl mb-4">NO COUPONS CREATED</div>
          <button
            onClick={() => setShowModal(true)}
            className="bg-yellow-400 text-black px-6 py-3 font-black border-2 border-black hover:bg-yellow-300"
          >
            CREATE YOUR FIRST COUPON
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {discounts.map((discount) => {
            const isExpired = new Date(discount.validUntil) < new Date();
            const usagePercent = discount.maxUses ? (discount.usedCount / discount.maxUses) * 100 : 0;

            return (
              <div
                key={discount._id}
                className={`bg-white border-4 border-black p-6 shadow-[4px_4px_0px_black] ${
                  !discount.isActive || isExpired ? 'opacity-60' : ''
                }`}
              >
                <div className="flex flex-wrap gap-4 justify-between items-start">
                  {/* Code & Type */}
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="bg-black text-yellow-400 px-4 py-2 font-mono font-black text-xl">
                        {discount.code}
                      </div>
                      {!discount.isActive && (
                        <span className="bg-gray-500 text-white px-2 py-1 text-xs font-bold">INACTIVE</span>
                      )}
                      {isExpired && (
                        <span className="bg-red-500 text-white px-2 py-1 text-xs font-bold">EXPIRED</span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600">{discount.description || 'No description'}</div>
                  </div>

                  {/* Discount Value */}
                  <div className="text-center">
                    <div className="text-4xl font-black">
                      {discount.type === 'percentage' ? `${discount.value}%` : `₹${discount.value}`}
                    </div>
                    <div className="text-xs font-bold text-gray-500 uppercase">
                      {discount.type === 'percentage' ? 'OFF' : 'FLAT OFF'}
                    </div>
                  </div>

                  {/* Usage */}
                  <div className="text-center">
                    <div className="text-2xl font-black">
                      {discount.usedCount}{discount.maxUses ? `/${discount.maxUses}` : ''}
                    </div>
                    <div className="text-xs font-bold text-gray-500 uppercase">USES</div>
                    {discount.maxUses && (
                      <div className="w-24 h-2 bg-gray-200 mt-2">
                        <div 
                          className={`h-full ${usagePercent > 80 ? 'bg-red-500' : 'bg-green-500'}`}
                          style={{ width: `${Math.min(usagePercent, 100)}%` }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Validity */}
                  <div className="text-center">
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar size={16} />
                      <span className="font-bold">{formatDate(discount.validFrom)}</span>
                    </div>
                    <div className="text-xs text-gray-500">to</div>
                    <div className="font-bold text-sm">{formatDate(discount.validUntil)}</div>
                  </div>

                  {/* Min Purchase */}
                  <div className="text-center">
                    <div className="text-xl font-black">₹{discount.minPurchase}</div>
                    <div className="text-xs font-bold text-gray-500 uppercase">MIN ORDER</div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => toggleActive(discount)}
                      className={`p-3 border-2 border-black ${
                        discount.isActive ? 'bg-green-400' : 'bg-gray-200'
                      }`}
                      title={discount.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {discount.isActive ? <Check size={20} /> : <X size={20} />}
                    </button>
                    <button
                      onClick={() => {
                        setEditingDiscount(discount);
                        setShowModal(true);
                      }}
                      className="p-3 bg-blue-500 text-white border-2 border-black"
                    >
                      <Edit size={20} />
                    </button>
                    <button
                      onClick={() => handleDelete(discount._id)}
                      className="p-3 bg-red-500 text-white border-2 border-black"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>

                {/* Categories */}
                {discount.applicableCategories?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <span className="text-xs font-bold text-gray-500">APPLICABLE ON: </span>
                    {discount.applicableCategories.map((cat) => (
                      <span key={cat} className="inline-block bg-gray-100 px-2 py-1 text-xs font-bold mr-2 border border-gray-300 uppercase">
                        {cat}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <DiscountModal
          discount={editingDiscount}
          onClose={() => {
            setShowModal(false);
            setEditingDiscount(null);
          }}
          onSuccess={() => {
            fetchDiscounts();
            setShowModal(false);
            setEditingDiscount(null);
          }}
        />
      )}
    </div>
  );
};

// Discount Modal Component
const DiscountModal = ({ discount, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    code: discount?.code || '',
    type: discount?.type || 'percentage',
    value: discount?.value || '',
    minPurchase: discount?.minPurchase || 0,
    maxUses: discount?.maxUses || '',
    validFrom: discount?.validFrom ? new Date(discount.validFrom).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    validUntil: discount?.validUntil ? new Date(discount.validUntil).toISOString().split('T')[0] : '',
    description: discount?.description || '',
    applicableCategories: discount?.applicableCategories || [],
    isActive: discount?.isActive !== undefined ? discount.isActive : true
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        ...formData,
        code: formData.code.toUpperCase(),
        maxUses: formData.maxUses || null
      };

      if (discount) {
        await api.put(`/discounts/admin/${discount._id}`, data);
      } else {
        await api.post('/discounts/admin', data);
      }
      
      onSuccess();
    } catch (error) {
      alert('Error: ' + error.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', 'anime', 'tantra', 'inkspeak', 'soul'];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white border-4 border-yellow-400 shadow-[16px_16px_0px_rgba(255,255,0,1)] w-full max-w-lg my-8">
        <div className="bg-yellow-400 p-4 border-b-4 border-black flex justify-between items-center">
          <h2 className="text-2xl font-black">{discount ? 'EDIT COUPON' : 'CREATE COUPON'}</h2>
          <button onClick={onClose} className="text-2xl font-black hover:text-red-600">×</button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Code */}
          <div>
            <label className="block text-xs font-black uppercase mb-2">Coupon Code*</label>
            <input
              type="text"
              required
              value={formData.code}
              onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
              placeholder="OWLFIT20"
              className="w-full px-4 py-3 border-2 border-black font-mono uppercase"
            />
          </div>

          {/* Type & Value */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-black uppercase mb-2">Type*</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({...formData, type: e.target.value})}
                className="w-full px-4 py-3 border-2 border-black font-bold bg-white"
              >
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed Amount (₹)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-black uppercase mb-2">Value*</label>
              <input
                type="number"
                required
                min="0"
                max={formData.type === 'percentage' ? 100 : undefined}
                value={formData.value}
                onChange={(e) => setFormData({...formData, value: e.target.value})}
                placeholder={formData.type === 'percentage' ? '20' : '500'}
                className="w-full px-4 py-3 border-2 border-black font-bold"
              />
            </div>
          </div>

          {/* Min Purchase & Max Uses */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-black uppercase mb-2">Min Purchase (₹)</label>
              <input
                type="number"
                min="0"
                value={formData.minPurchase}
                onChange={(e) => setFormData({...formData, minPurchase: e.target.value})}
                placeholder="0"
                className="w-full px-4 py-3 border-2 border-black font-bold"
              />
            </div>
            <div>
              <label className="block text-xs font-black uppercase mb-2">Max Uses (leave empty = unlimited)</label>
              <input
                type="number"
                min="1"
                value={formData.maxUses}
                onChange={(e) => setFormData({...formData, maxUses: e.target.value})}
                placeholder="Unlimited"
                className="w-full px-4 py-3 border-2 border-black font-bold"
              />
            </div>
          </div>

          {/* Validity */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-black uppercase mb-2">Valid From*</label>
              <input
                type="date"
                required
                value={formData.validFrom}
                onChange={(e) => setFormData({...formData, validFrom: e.target.value})}
                className="w-full px-4 py-3 border-2 border-black font-bold"
              />
            </div>
            <div>
              <label className="block text-xs font-black uppercase mb-2">Valid Until*</label>
              <input
                type="date"
                required
                value={formData.validUntil}
                onChange={(e) => setFormData({...formData, validUntil: e.target.value})}
                className="w-full px-4 py-3 border-2 border-black font-bold"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-black uppercase mb-2">Description</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Get 20% off on your first order"
              className="w-full px-4 py-3 border-2 border-black"
            />
          </div>

          {/* Categories */}
          <div>
            <label className="block text-xs font-black uppercase mb-2">Applicable Categories</label>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <label key={cat} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.applicableCategories.includes(cat)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({...formData, applicableCategories: [...formData.applicableCategories, cat]});
                      } else {
                        setFormData({...formData, applicableCategories: formData.applicableCategories.filter(c => c !== cat)});
                      }
                    }}
                    className="w-4 h-4"
                  />
                  <span className="font-bold uppercase text-sm">{cat}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Active */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
              className="w-5 h-5"
            />
            <span className="font-bold">Active (customers can use this code)</span>
          </label>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-black text-lg uppercase py-4 border-4 border-black shadow-[8px_8px_0px_rgba(0,0,0,1)] hover:translate-x-1 hover:translate-y-1 hover:shadow-[4px_4px_0px_rgba(0,0,0,1)] transition-all disabled:opacity-50"
          >
            {loading ? 'SAVING...' : (discount ? 'UPDATE COUPON' : 'CREATE COUPON')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default DiscountManagement;
